import { useCallback, useEffect, useRef, useState } from 'react'
import { Archive, ChevronRight, Pencil, Plus } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { fetchInsightsWithSignal, INSIGHTS_CHANGED_EVENT } from '../app/api/insightsApi'
import { apiFetch } from '../app/auth/apiClient'
import { useSidebar } from '../app/components/navigation/DesignerSidebar'
import sidebarStyles from '../app/components/navigation/DesignerSidebar.module.scss'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../app/components/ui/tooltip'
import { cn } from '../app/lib/utils'
import { useStoredFlag } from '../app/shell/useStoredFlag'
import {
  newConversationId,
  onChatConversationSelected,
  onChatHistoryChanged,
  savedConversationId,
  selectChatConversation,
} from './useCodexChat'

type ChatConversationSummary = {
  kind: 'chat'
  id: string
  title: string
  updatedAt: string
}

type InsightConversationSummary = {
  kind: 'insight'
  id: string
  title: string
  updatedAt: string
}

type ConversationSummary = ChatConversationSummary | InsightConversationSummary

function chatConversationList(value: unknown): ChatConversationSummary[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (
      typeof item === 'object' &&
      item !== null &&
      typeof (item as { id?: unknown }).id === 'string' &&
      typeof (item as { title?: unknown }).title === 'string' &&
      typeof (item as { updated_at?: unknown }).updated_at === 'string'
    ) return [{
      kind: 'chat' as const,
      id: (item as { id: string }).id,
      title: (item as { title: string }).title,
      updatedAt: (item as { updated_at: string }).updated_at,
    }]
    return []
  })
}

function useChatHistory(isOpen: boolean) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [currentConversationId, setCurrentConversationId] = useState(savedConversationId)
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [updatingConversationId, setUpdatingConversationId] = useState<string | null>(null)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const renameInFlightRef = useRef(false)
  const skipRenameRef = useRef(false)

  const loadConversations = useCallback(async (signal: AbortSignal) => {
    const [chatResult, insightResult] = await Promise.allSettled([
      apiFetch('/api/chat/conversations', { signal }).then(async (response) => {
        if (!response.ok) return []
        const payload = (await response.json()) as { conversations?: unknown }
        return chatConversationList(payload.conversations)
      }),
      fetchInsightsWithSignal(signal).then((insights) => insights.map((insight) => ({
        kind: 'insight' as const,
        id: insight.fingerprint,
        title: insight.title,
        updatedAt: insight.messages.at(-1)?.createdAt ?? insight.computedAt,
      }))),
    ])
    if (signal.aborted) return
    const next: ConversationSummary[] = [
      ...(chatResult.status === 'fulfilled' ? chatResult.value : []),
      ...(insightResult.status === 'fulfilled' ? insightResult.value : []),
    ]
    setConversations(next.sort(
      (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
    ))
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadConversations(controller.signal)
    return () => controller.abort()
  }, [isOpen, loadConversations, refreshVersion])

  useEffect(() => onChatConversationSelected(setCurrentConversationId), [])
  useEffect(() => onChatHistoryChanged(() => setRefreshVersion((version) => version + 1)), [])
  useEffect(() => {
    const refresh = () => setRefreshVersion((version) => version + 1)
    window.addEventListener(INSIGHTS_CHANGED_EVENT, refresh)
    return () => window.removeEventListener(INSIGHTS_CHANGED_EVENT, refresh)
  }, [])

  const updateConversation = useCallback(
    async (conversationId: string, update: { title: string } | { archived: true }) => {
      setUpdatingConversationId(conversationId)
      try {
        const response = await apiFetch(`/api/chat/${encodeURIComponent(conversationId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update),
        })
        if (!response.ok) throw new Error(`Errore ${response.status}`)
        if ('title' in update) {
          setConversations((current) =>
            current.map((conversation) =>
              conversation.kind === 'chat' && conversation.id === conversationId
                ? { ...conversation, title: update.title }
                : conversation,
            ),
          )
        } else {
          setConversations((current) => current.filter((conversation) =>
            conversation.kind !== 'chat' || conversation.id !== conversationId,
          ))
          if (conversationId === currentConversationId) {
            selectChatConversation(newConversationId(), { newConversation: true })
          }
        }
        return true
      } catch {
        return false
      } finally {
        setUpdatingConversationId(null)
      }
    },
    [currentConversationId],
  )

  function beginRename(conversation: ChatConversationSummary) {
    skipRenameRef.current = false
    setDraftTitle(conversation.title)
    setEditingConversationId(conversation.id)
  }

  function cancelRename() {
    skipRenameRef.current = true
    setEditingConversationId(null)
  }

  async function commitRename(conversation: ChatConversationSummary) {
    if (skipRenameRef.current) {
      skipRenameRef.current = false
      return
    }
    if (renameInFlightRef.current) return
    const title = draftTitle.trim()
    setEditingConversationId(null)
    if (!title || title === conversation.title) return
    renameInFlightRef.current = true
    try {
      await updateConversation(conversation.id, { title })
    } finally {
      renameInFlightRef.current = false
    }
  }

  return {
    conversations,
    currentConversationId,
    editingConversationId,
    draftTitle,
    updatingConversationId,
    setDraftTitle,
    beginRename,
    cancelRename,
    commitRename,
    archiveConversation: (conversationId: string) =>
      updateConversation(conversationId, { archived: true }),
  }
}

export function ChatHistorySection({ conversationVisible = false }: {
  conversationVisible?: boolean
}) {
  const { collapsed } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useStoredFlag('progetto_arca.sidebar.tasks.open', false)
  const {
    conversations,
    currentConversationId,
    editingConversationId,
    draftTitle,
    updatingConversationId,
    setDraftTitle,
    beginRename,
    cancelRename,
    commitRename,
    archiveConversation,
  } = useChatHistory(isOpen)

  return (
    <TooltipProvider delayDuration={0}>
      <div className={sidebarStyles.chatSection}>
        <div className={sidebarStyles.chatDivider} aria-hidden />

        <button
          type="button"
          className={sidebarStyles.chatNewButton}
          onClick={() =>
            selectChatConversation(newConversationId(), {
              workspace: true,
              newConversation: true,
            })}
          aria-label="Nuova conversazione"
          title={collapsed ? 'Nuova conversazione' : undefined}
        >
          <Plus className={sidebarStyles.itemIcon} aria-hidden />
          {!collapsed ? <span className={sidebarStyles.itemLabel}>Nuova conversazione</span> : null}
        </button>

        <section className={sidebarStyles.group} hidden={collapsed || conversations.length === 0}>
          <button
            type="button"
            className={sidebarStyles.groupButton}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-label="Conversazioni recenti"
          >
            <span className={sidebarStyles.groupLabel}>Conversazioni recenti</span>
            <ChevronRight
              className={cn(sidebarStyles.groupChevron, isOpen && sidebarStyles.groupChevronOpen)}
              aria-hidden
            />
          </button>

          <div className={sidebarStyles.chatHistoryPanel} hidden={!isOpen}>
            {conversations.map((conversation) => {
              const active = conversation.kind === 'chat'
                ? conversationVisible && conversation.id === currentConversationId
                : location.pathname === '/chat' &&
                  new URLSearchParams(location.search).get('insight') === conversation.id
              return (
                <div
                  key={`${conversation.kind}:${conversation.id}`}
                  className={cn(
                    sidebarStyles.chatSessionItem,
                    active && sidebarStyles.chatSessionActive,
                  )}
                >
                  {conversation.kind === 'chat' && editingConversationId === conversation.id ? (
                    <div className={sidebarStyles.chatSessionButton}>
                      <input
                        autoFocus
                        className={sidebarStyles.chatSessionTitleInput}
                        value={draftTitle}
                        maxLength={80}
                        aria-label="Titolo chat"
                        onBlur={() => void commitRename(conversation)}
                        onChange={(event) => setDraftTitle(event.target.value)}
                        onFocus={(event) => event.currentTarget.select()}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault()
                            void commitRename(conversation)
                          } else if (event.key === 'Escape') {
                            event.preventDefault()
                            cancelRename()
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={sidebarStyles.chatSessionButton}
                      title={conversation.title}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => {
                        if (conversation.kind === 'chat') {
                          selectChatConversation(conversation.id, { workspace: true })
                          return
                        }
                        const returnTo = location.pathname === '/chat'
                          ? '/insights?view=history'
                          : `${location.pathname}${location.search}${location.hash}`
                        navigate(`/chat?insight=${encodeURIComponent(conversation.id)}`, {
                          state: { lumaReturnTo: returnTo },
                        })
                      }}
                      onDoubleClick={conversation.kind === 'chat'
                        ? () => beginRename(conversation)
                        : undefined}
                    >
                      <span className={sidebarStyles.chatSessionTitle}>{conversation.title}</span>
                    </button>
                  )}
                  {conversation.kind === 'chat' ? <span className={sidebarStyles.chatSessionActions}>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={sidebarStyles.chatSessionActionButton}
                          aria-label={`Rinomina conversazione: ${conversation.title}`}
                          disabled={
                            updatingConversationId === conversation.id ||
                            editingConversationId === conversation.id
                          }
                          onClick={() => beginRename(conversation)}
                        >
                          <Pencil size={14} aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Rinomina</TooltipContent>
                    </Tooltip>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={sidebarStyles.chatSessionActionButton}
                          aria-label={`Archivia conversazione: ${conversation.title}`}
                          disabled={
                            updatingConversationId === conversation.id ||
                            editingConversationId === conversation.id
                          }
                          onClick={() => void archiveConversation(conversation.id)}
                        >
                          <Archive size={14} aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Archivia</TooltipContent>
                    </Tooltip>
                  </span> : null}
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </TooltipProvider>
  )
}
